/**
 * Banner with Expanding Media - Scroll Animation
 * Expands video to fit container on scroll (>= 992px screens only)
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin( ScrollTrigger );

const BannerScrollBehavior = ( function () {
  'use strict';

  const BREAKPOINT = 992;
  const BUFFER_HEIGHT = 80;
  const BUFFER_WIDTH = 40;

  // Get all required DOM elements
  function getAnimationElements() {
    const section = document.querySelector( '.e-stn--banner-with-expanding-media' );
    
    return {
      section,
      videoColumn: section?.querySelector( '.e-col--video-wrapper' ),
      videoWrapper: section?.querySelector( '.e-col--video-wrapper > .e-div' ),
      videoContainer: section?.querySelector( '.e-vid' ),
      textContent: section?.querySelector( '.e-col:not(.e-col--video-wrapper)' ),
      container: section?.querySelector( '.container' ),
      header: document.querySelector( 'header' ),
    };
  }

  // Calculate dimensions and rectangles. All DOM reads are batched here,
  // before any style writes happen, so the browser only needs one layout
  // pass instead of one per read/write pair (layout thrashing).
  function calculateDimensions( elements ) {
    const { container, videoWrapper, section, header, textContent, videoColumn } = elements;

    return {
      containerRect: container.getBoundingClientRect(),
      videoRect: videoWrapper.getBoundingClientRect(),
      sectionRect: section.getBoundingClientRect(),
      headerHeight: header ? header.offsetHeight : 0,
      sectionHeight: section.offsetHeight,
      textHeight: textContent ? textContent.offsetHeight : 0,
      videoPaddingTop: parseFloat( window.getComputedStyle( videoColumn ).paddingTop ) || 0,
      viewportHeight: window.innerHeight,
      scrollY: window.scrollY,
    };
  }

  // Calculate scale for video expansion
  function calculateVideoScale( dimensions ) {
    const { containerRect, videoRect, headerHeight } = dimensions;
    const availableHeight = window.innerHeight - headerHeight - BUFFER_HEIGHT;
    const availableWidth = containerRect.width - BUFFER_WIDTH;
    
    const scaleX = availableWidth / videoRect.width;
    const scaleY = availableHeight / videoRect.height;
    const scale = Math.min( scaleX, scaleY );
    
    const expandedWidth = videoRect.width * scale;
    const finalWidth = Math.min( expandedWidth, containerRect.width - BUFFER_WIDTH );
    
    return { scale, finalWidth };
  }

  // Calculate translation values
  function calculateTranslation( dimensions, finalWidth ) {
    const { containerRect, videoRect } = dimensions;
    const containerCenterX = containerRect.left + ( containerRect.width / 2 );
    const videoCenterX = videoRect.left + ( videoRect.width / 2 );
    const finalVideoCenterX = videoCenterX + ( ( finalWidth - videoRect.width ) / 2 );
    
    return containerCenterX - finalVideoCenterX;
  }

  // Calculate scroll distance
  function calculateScrollDistance( dimensions ) {
    const { videoRect, sectionRect, headerHeight, scrollY } = dimensions;
    const videoAbsoluteTop = videoRect.top + scrollY;
    const sectionAbsoluteTop = sectionRect.top + scrollY;
    const videoTargetTop = sectionAbsoluteTop + headerHeight;
    
    return videoAbsoluteTop - videoTargetTop;
  }

  // Create text animation
  function createTextAnimation( tl, elements, dimensions ) {
    const { textContent } = elements;

    if ( !textContent ) {
      return;
    }

    const { textHeight, videoPaddingTop } = dimensions;

    tl.to( textContent, {
      y: -( textHeight + videoPaddingTop + 50 ),
      ease: 'power1.in',
      duration: 0.4,
    }, 0 );
  }

  // Create video animation
  function createVideoAnimation( tl, elements, scale, translateX ) {
    const { videoWrapper, videoContainer } = elements;
    
    tl.to( videoWrapper, {
      scale,
      x: translateX,
      y: 0,
      transformOrigin: 'bottom left',
      ease: 'power1.inOut',
      duration: 1,
    }, 0 );

    tl.to( videoContainer, {
      '--overlay-opacity': 0,
      ease: 'none',
      duration: 0.5,
    }, 0.2 );
  }

  // Check if elements are valid
  function areElementsValid( elements ) {
    const { section, videoWrapper, videoContainer, videoColumn } = elements;
    return Boolean( section && videoWrapper && videoContainer && videoColumn );
  }

  const SCROLL_WRAPPER_CLASS = 'e-div--banner-scroll-wrapper';

  // Setup the main animation
  function setupAnimation() {
    if ( window.innerWidth < BREAKPOINT ) {
      return;
    }

    const elements = getAnimationElements();
    
    if ( !areElementsValid( elements ) ) {
      return;
    }

    const dimensions = calculateDimensions( elements );
    const { scale, finalWidth } = calculateVideoScale( dimensions );
    const translateX = calculateTranslation( dimensions, finalWidth );
    const scrollDistance = calculateScrollDistance( dimensions );

    // sectionHeight was already read (pre-mutation) in calculateDimensions.
    // Reading it here instead would force a synchronous layout, since the
    // minHeight write below has already invalidated it.
    const rawOverflow = dimensions.sectionHeight - dimensions.viewportHeight;
    elements.section.style.minHeight = `${ dimensions.viewportHeight }px`;
    const overflow = rawOverflow > 5 ? rawOverflow : 0;

    let scrollWrapper = elements.section.closest( `.${ SCROLL_WRAPPER_CLASS }` );
    if ( !scrollWrapper ) {
      scrollWrapper = document.createElement( 'div' );
      scrollWrapper.className = SCROLL_WRAPPER_CLASS;
      elements.section.parentNode.insertBefore( scrollWrapper, elements.section );
      scrollWrapper.appendChild( elements.section );
    }

    scrollWrapper.style.height = `${ dimensions.viewportHeight + overflow + scrollDistance }px`;
    scrollWrapper.style.background = 'inherit';
    elements.section.style.position = 'sticky';
    elements.section.style.top = '0';

    if ( overflow > 0 ) {
      gsap.to( elements.section, {
        top: -overflow,
        ease: 'none',
        scrollTrigger: {
          trigger: scrollWrapper,
          start: 'top top',
          end: `+=${ overflow }`,
          scrub: 1,
          invalidateOnRefresh: true,
        },
      } );
    }

    const tl = gsap.timeline( {
      scrollTrigger: {
        trigger: scrollWrapper,
        start: overflow > 0 ? `top+=${ overflow } top` : 'top top',
        end: `+=${ scrollDistance }`,
        scrub: 1,
        invalidateOnRefresh: true,
      },
    } );

    createTextAnimation( tl, elements, dimensions );
    createVideoAnimation( tl, elements, scale, translateX );
  }

  // Kill ScrollTriggers for the banner section
  function killScrollTriggersForSection() {
    ScrollTrigger.getAll().forEach( ( st ) => {
      if (
        st.trigger?.classList?.contains( 'e-stn--banner-with-expanding-media' ) ||
        st.trigger?.classList?.contains( SCROLL_WRAPPER_CLASS )
      ) {
        st.kill();
      }
    } );
  }

  // Reset section styles
  function resetSectionStyles() {
    const section = document.querySelector( '.e-stn--banner-with-expanding-media' );
    if ( !section ) {
      return;
    }

    section.style.minHeight = '';
    section.style.position = '';
    section.style.top = '';
    section.style.height = '';

    const wrapper = section.closest( `.${ SCROLL_WRAPPER_CLASS }` );
    if ( wrapper ) {
      wrapper.parentNode.insertBefore( section, wrapper );
      wrapper.remove();
    }
  }

  // Handle resize events
  function handleResize() {
    const matchesBreakpoint = window.innerWidth >= BREAKPOINT;

    killScrollTriggersForSection();

    if ( matchesBreakpoint ) {
      setupAnimation();
    } else {
      resetSectionStyles();
    }
    
    initVideoPlayback();
  }

  // Initialize banner scroll animation
  function initBannerScrollAnimation() {
    setupAnimation();

    let resizeTimer;
    window.addEventListener( 'resize', () => {
      clearTimeout( resizeTimer );
      resizeTimer = setTimeout( handleResize, 250 );
    } );
  }

  // Setup video element for autoplay
  function setupVideoElement( videoElement ) {
    if ( !videoElement ) {
      return;
    }
    
    videoElement.muted = true;
    videoElement.loop = true;
    videoElement.setAttribute( 'playsinline', 'true' );
    videoElement.play().catch( () => {} );
  }

  // Initialize video playback (works on all devices)
  function initVideoPlayback() {
    const section = document.querySelector( '.e-stn--banner-with-expanding-media' );
    const videoElement = section?.querySelector( '.e-vid video' );
    setupVideoElement( videoElement );

    const backgroundWrapper = document.querySelector( '.e-div--gradient-background' );
    const bgVideoElement = backgroundWrapper?.querySelector( '.e-bgv video' );
    setupVideoElement( bgVideoElement );
  }

  // Initialize function
  function init() {
    initVideoPlayback();
    initBannerScrollAnimation();
  }

  return { init };
} )();

// Initialize when DOM is ready
if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', BannerScrollBehavior.init );
} else {
  BannerScrollBehavior.init();
}
